/**
 * The new-request window — one composer, raised from anywhere in the client portal.
 *
 * Until November 2026 this was a card halfway down `/app/requests`, and every way into
 * it was a link to that card: the topbar's *Request work*, Projects' *Start a project*.
 * Now it is a window over whatever page the client is on, opened by the rail's *New
 * request* (`shell/NewRequestButton.tsx`) as well as by those two, so asking for work
 * is one press from every screen.
 *
 * **It asks for two things.** What is wanted, in a line, and which service it is work
 * on — the second because a request is filed against a rate-card service
 * (`work_order.service_code` is `not null`) and the studio prices it from there; the
 * hint says the studio can correct it, which is true (`work_orders_update` takes a
 * service). The description, the files and the urgency are offered and never
 * required: urgency has a default and the rest may be empty.
 *
 * **The draft survives the window.** The form and the picked files live here, in the
 * provider the shell mounts, rather than in the window — so closing it by accident, or
 * to look something up on another page, loses nothing, and that is also why a click
 * beside it is allowed to close it. Leaving the account (the org switcher) is the one
 * thing that throws the draft away, because a draft written for one client must not
 * be sent to the next: `/api/clients/me/requests` is whichever account is active.
 *
 * **Files go after the request, and the window can be closed while they do.** A
 * request needs a reference before a file can be attached to it (`requestFiles.ts`
 * says why one at a time), and a video can take minutes. So the request is created,
 * the window turns into its receipt with a progress bar per file, and the client may
 * close it and keep working: the rail says how far the files have got, the tab asks
 * before it is closed on them, and *Try again* re-sends only what failed.
 *
 * **A role that cannot raise a request gets no composer at all** — `.docs/19` line 123,
 * a control somebody would be refused for is absent — so `useRequestComposer` answers
 * null for it and every caller renders nothing.
 */

import { DraftWorkOrderSchema, type WorkOrderTiming } from '@fareast/shared'
import { Link } from '@tanstack/react-router'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type ReactNode,
} from 'react'
import type { Control } from 'react-hook-form'
import { z } from 'zod'

import { useCatalog, useDraftRequest, useUploadAttachment } from '../../../api/client/index.js'
import { messageOf } from '../../../api/problem.js'
import { usePrincipal } from '../../../auth/principal.js'
import { Button, ButtonRow, Seg, SegItem } from '../../../components/portal/Button.js'
import { FileRow } from '../../../components/portal/Files.js'
import {
  Drop,
  Field,
  Hint,
  Input,
  Select,
  Textarea,
  useZodForm,
} from '../../../components/portal/Form.js'
import { Stack } from '../../../components/portal/Layout.js'
import { Meter } from '../../../components/portal/Meter.js'
import { Dialog } from '../../../components/portal/Overlay.js'
import { Notice } from '../../../components/portal/Status.js'
import { Muted, Prose, Ref, Small } from '../../../components/portal/Text.js'
import { useFormat } from '../../../format/useFormat.js'
import { extensionOf } from '../files/fileType.js'
import { abilitiesOf } from '../settings/roles.js'
import {
  changed,
  isGoing,
  picked,
  progressOf,
  queued,
  retried,
  type PickedFile,
  type Progress,
  type SentFile,
} from './requestFiles.js'
import { TIMING_HINT, TIMING_WORD, TIMINGS } from './words.js'

/** The window's title, and the rail's button. */
export const NEW_REQUEST_LABEL = 'New request'

/**
 * The four fields this form has, of the eight `DraftWorkOrderSchema` takes.
 *
 * The three it leaves out are the billing page's: `catalogue_codes` and
 * `preferred_start_on` belong to the ticked rate card, and `parent_ref` is
 * `work_orders_add_scope_change`'s. `kind` is the API's — a client raises an extra,
 * and the route sets it.
 *
 * **Two of them are the API's own checks with one sentence in front.** The service's
 * unset state is `''`, which `CatalogCodeSchema` refuses with "A catalog code is
 * lowercase words joined by underscores" — a sentence about a column, printed at a
 * client who has simply not chosen from a list. The title's floor is three
 * characters, and zod's own words for it are about a string's length. The pipes keep
 * the API's rule for every value that gets past the sentence.
 */
const ComposerSchema = DraftWorkOrderSchema.omit({
  kind: true,
  catalogue_codes: true,
  preferred_start_on: true,
  parent_ref: true,
}).extend({
  title: z
    .string()
    .trim()
    .min(3, 'Say what you need in a few words.')
    .pipe(DraftWorkOrderSchema.shape.title),
  service: z
    .string()
    .min(1, 'Choose the kind of work this is.')
    .pipe(DraftWorkOrderSchema.shape.service),
})

type ComposerInput = z.input<typeof ComposerSchema>

/** The form as it opens, and as it is left after a request has gone. */
const BLANK: ComposerInput = {
  service: '',
  title: '',
  scope: '',
  timing: 'this_month',
  source: 'composer',
}

/** A request that exists, and its files. */
type Sent = { readonly ref: string; readonly files: readonly SentFile[] }

/** What the rest of the portal may do with the composer. */
export type RequestComposerHandle = { readonly open: () => void }

/**
 * The last request's files, for the rail: while any is on its way, and after, while any
 * did not arrive — a failure nobody is shown is a file the studio never gets. Null
 * otherwise.
 */
export type RequestSending = Progress & { readonly ref: string }

const HandleContext = createContext<RequestComposerHandle | null | undefined>(undefined)
const SendingContext = createContext<RequestSending | null>(null)

/**
 * The composer, or null for a reader who may not raise a request.
 *
 * Two contexts rather than one, because the progress changes several times a second
 * while a video goes up and the pages that only open the window — Requests, Projects —
 * should not be drawn again for every one of them.
 */
export function useRequestComposer(): RequestComposerHandle | null {
  const handle = useContext(HandleContext)
  if (handle === undefined) {
    throw new Error('useRequestComposer needs a RequestComposerProvider; PortalLayout mounts one.')
  }
  return handle
}

/** How far the last request's files have got, or null when there is nothing to say. */
export function useRequestSending(): RequestSending | null {
  return useContext(SendingContext)
}

/**
 * One element tree whatever the answer, so that a seat switched to one that may not
 * raise a request (the org switcher, owner of one client and billing on another) does
 * not remount the shell, the page and the switcher it was pressed on.
 */
export function RequestComposerProvider({ children }: { readonly children: ReactNode }) {
  const principal = usePrincipal()
  const account = principal.kind === 'client_user' ? (principal.clientId ?? '') : ''
  return (
    <Composer account={account} allowed={abilitiesOf(principal).raisesRequests}>
      {children}
    </Composer>
  )
}

function Composer({
  account,
  allowed,
  children,
}: {
  readonly account: string
  readonly allowed: boolean
  readonly children: ReactNode
}) {
  const draft = useDraftRequest()
  const upload = useUploadAttachment()

  const [open, setOpen] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const form = useZodForm(ComposerSchema, { defaultValues: BLANK })

  /*
   * The request that was sent and its files, in state for drawing and in a ref for
   * the loop that sends them: an `await` inside a loop reads the state it started
   * with, and a file added or retried while another is going would never be seen.
   */
  const [sent, setSentState] = useState<Sent | null>(null)
  const latest = useRef<Sent | null>(null)
  const setSent = useCallback((next: (now: Sent | null) => Sent | null) => {
    latest.current = next(latest.current)
    setSentState(latest.current)
  }, [])

  /*
   * The draft's files, the same way and for a reason of the same kind: a file dropped
   * while the request is being created has to be among the ones sent once it exists,
   * and the submit handler's own copy of the list is the one it was pressed with.
   */
  const [files, setFilesState] = useState<readonly PickedFile[]>([])
  const pickedNow = useRef<readonly PickedFile[]>([])
  const setFiles = useCallback((next: (now: readonly PickedFile[]) => readonly PickedFile[]) => {
    pickedNow.current = next(pickedNow.current)
    setFilesState(pickedNow.current)
  }, [])

  /*
   * Leaving the account calls off what is on its way and throws the draft away — see
   * the header. The controller is made in the effect, not in a ref's initialiser, so
   * StrictMode's rehearsal unmount aborts a controller nothing has used yet.
   */
  const signal = useRef<AbortSignal | null>(null)
  const { reset } = form
  useEffect(() => {
    const controller = new AbortController()
    signal.current = controller.signal
    setOpen(false)
    setFiles(() => [])
    setFailure(null)
    setSent(() => null)
    reset(BLANK)
    return () => {
      controller.abort()
    }
  }, [account, reset, setSent, setFiles])

  const pumping = useRef(false)

  /** Sends every waiting file of `ref`, one at a time, until none is left waiting. */
  async function pump(ref: string): Promise<void> {
    if (pumping.current) return
    pumping.current = true
    try {
      for (;;) {
        const now = latest.current
        const next = now?.ref === ref ? now.files.find((one) => one.state === 'waiting') : undefined
        const off = signal.current
        if (next === undefined || off === null || off.aborted) return
        await sendOne(ref, next, off)
      }
    } finally {
      pumping.current = false
    }
  }

  async function sendOne(ref: string, one: SentFile, off: AbortSignal): Promise<void> {
    const mark = (change: Parameters<typeof changed>[2]) => {
      setSent((now) =>
        now?.ref === ref ? { ref, files: changed(now.files, one.key, change) } : now,
      )
    }
    mark({ state: 'sending', sent: 0 })
    try {
      await upload.mutateAsync({
        ref,
        file: one.file,
        signal: off,
        onProgress: (bytes) => {
          mark({ sent: bytes })
        },
      })
      mark({ state: 'attached', sent: one.file.size })
    } catch (error) {
      if (off.aborted) return
      mark({ state: 'failed', reason: messageOf(error) })
    }
  }

  /** More files — onto the draft, or, once it is sent, straight onto the request. */
  function add(more: readonly File[]): void {
    if (more.length === 0) return
    const ref = latest.current?.ref
    if (ref === undefined) {
      setFiles((now) => [...now, ...picked(more)])
      return
    }
    setSent((now) => (now === null ? now : { ref, files: [...now.files, ...queued(picked(more))] }))
    void pump(ref)
  }

  async function submit(values: z.output<typeof ComposerSchema>): Promise<void> {
    setFailure(null)
    let ref: string
    try {
      const created = await draft.mutateAsync({ ...values, catalogue_codes: [] })
      ref = created.work_order.ref
    } catch (error) {
      setFailure(messageOf(error))
      return
    }
    /*
     * The request exists from here on, so nothing below may leave a form whose
     * button drafts a second one: the draft is cleared before a byte is sent.
     */
    setSent(() => ({ ref, files: queued(pickedNow.current) }))
    setFiles(() => [])
    form.reset(BLANK)
    // Not awaited: the form is done once the request exists, however long a video takes.
    void pump(ref)
  }

  /** Starts the next request: only offered once nothing of the last one is going. */
  const another = useCallback(() => {
    setSent(() => null)
    setFailure(null)
  }, [setSent])

  /*
   * A receipt with nothing left to say is not what the next press should open on —
   * asked when the window shuts and again when it opens, because the files may have
   * finished while it was shut.
   */
  const forgetFinished = useCallback(() => {
    const now = latest.current
    if (now !== null && !isGoing(now.files) && progressOf(now.files).failed === 0) another()
  }, [another])

  function onOpenChange(next: boolean): void {
    setOpen(next)
    if (!next) forgetFinished()
  }

  /* After *Raise another request*, the cursor goes where the next one starts. */
  const focusTitle = useRef(false)
  const { setFocus } = form
  useEffect(() => {
    if (!focusTitle.current || sent !== null) return
    focusTitle.current = false
    setFocus('title')
  }, [sent, setFocus])

  /*
   * A file dropped anywhere while the window is open is a file for it. Without this a
   * file let go a little outside the drop box lands on the page behind, and the
   * browser's own answer to that is to leave the portal and open the file — taking
   * the draft with it. `Drop`'s own handler prevents the default, which is how a drop
   * on the box is told from one beside it.
   */
  const adding = useRef(add)
  adding.current = add
  useEffect(() => {
    if (!open) return
    const over = (event: DragEvent) => {
      if (event.dataTransfer?.types.includes('Files') === true) event.preventDefault()
    }
    const drop = (event: DragEvent) => {
      if (event.defaultPrevented || event.dataTransfer === null) return
      if (!event.dataTransfer.types.includes('Files')) return
      event.preventDefault()
      adding.current([...event.dataTransfer.files])
    }
    window.addEventListener('dragover', over)
    window.addEventListener('drop', drop)
    return () => {
      window.removeEventListener('dragover', over)
      window.removeEventListener('drop', drop)
    }
  }, [open])

  /* Closing the tab on a file still going loses it, so the browser asks first. */
  const going = sent !== null && isGoing(sent.files)
  useEffect(() => {
    if (!going) return
    const ask = (event: BeforeUnloadEvent) => {
      event.preventDefault()
    }
    window.addEventListener('beforeunload', ask)
    return () => {
      window.removeEventListener('beforeunload', ask)
    }
  }, [going])

  const handle = useMemo<RequestComposerHandle>(
    () => ({
      open: () => {
        forgetFinished()
        setOpen(true)
      },
    }),
    [forgetFinished],
  )
  const sending = useMemo<RequestSending | null>(() => {
    if (sent === null) return null
    const progress = progressOf(sent.files)
    return progress.left > 0 || progress.failed > 0 ? { ref: sent.ref, ...progress } : null
  }, [sent])

  /**
   * A screenshot pasted into the window is a file for it; pasted text is still text.
   *
   * **Text wins when there is any.** Word and Excel put a picture of the selection on
   * the clipboard beside the text itself, so a brief pasted from a document would
   * otherwise arrive as `image.png` and the words would be lost. A screenshot, or a
   * file copied in the file manager, carries no text.
   */
  function onPaste(event: ClipboardEvent<HTMLElement>): void {
    const { types, files } = event.clipboardData
    if (types.includes('text/plain') || types.includes('text/html')) return
    const pasted = [...files]
    if (pasted.length === 0) return
    event.preventDefault()
    add(pasted)
  }

  const timing = form.watch('timing') ?? 'this_month'

  return (
    <HandleContext.Provider value={allowed ? handle : null}>
      <SendingContext.Provider value={allowed ? sending : null}>
        {children}
        {allowed ? (
          <Dialog
            open={open}
            onOpenChange={onOpenChange}
            title={NEW_REQUEST_LABEL}
            about={
              sent === null
                ? 'Tell us what you need. We price it and send it back for you to approve.'
                : undefined
            }
            wide
            fit
            held={draft.isPending}
          >
            {sent === null ? (
              <form
                onPaste={onPaste}
                onSubmit={(event) => {
                  event.preventDefault()
                  void form.handleSubmit(submit)(event)
                }}
              >
                <Stack>
                  <Input
                    control={form.control}
                    name="title"
                    label="What do you need?"
                    placeholder="Three reels for the November launch"
                  />

                  <ServiceField control={form.control} />

                  <Textarea
                    control={form.control}
                    name="scope"
                    label="Details (optional)"
                    placeholder="Anything that helps: what it is for, sizes, links, examples you like. Lists work — start one with the buttons above."
                    lists
                  />

                  <PickedFiles
                    files={files}
                    onAdd={add}
                    onRemove={(key) => {
                      setFiles((now) => now.filter((one) => one.key !== key))
                    }}
                  />

                  <Urgency
                    value={timing}
                    onChange={(value) => {
                      form.setValue('timing', value)
                    }}
                  />

                  {failure === null ? null : (
                    <Notice need role="alert">
                      {failure}
                    </Notice>
                  )}

                  <ButtonRow>
                    <Button type="submit" variant="primary" disabled={draft.isPending}>
                      {draft.isPending ? 'Sending…' : 'Send request'}
                    </Button>
                  </ButtonRow>
                  <Small>
                    <Muted>
                      Nothing here is a commitment. You approve the price when it comes back.
                    </Muted>
                  </Small>
                </Stack>
              </form>
            ) : (
              <Receipt
                sent={sent}
                onPaste={onPaste}
                onAdd={add}
                onRetry={() => {
                  setSent((now) => (now === null ? now : { ...now, files: retried(now.files) }))
                  void pump(sent.ref)
                }}
                onAnother={() => {
                  focusTitle.current = true
                  another()
                }}
                onLeave={() => {
                  onOpenChange(false)
                }}
              />
            )}
          </Dialog>
        ) : null}
      </SendingContext.Provider>
    </HandleContext.Provider>
  )
}

/**
 * Which service it is — the rate card's two halves as two named groups (R3-14), the
 * monthly card and project work, because `ClientCatalogSchema` answers them as two
 * arrays. Its own component so the rate card is read only when the window is open.
 *
 * A disabled control while the card loads, not a missing one: rendering nothing took
 * the field off the form and moved the tab order underneath anybody using it. A card
 * that could not be read is said here, with a way to ask again — `inline`, because
 * this window is over every page and its failure is not the page's.
 */
function ServiceField({
  control,
}: {
  readonly control: Control<ComposerInput, unknown, z.output<typeof ComposerSchema>>
}) {
  const catalog = useCatalog({ inline: true })
  const failed = catalog.isError && catalog.data === undefined
  const options = [
    { label: 'Your monthly package', options: choicesOf(catalog.data?.monthly) },
    { label: 'Project work', options: choicesOf(catalog.data?.projects) },
  ].filter((group) => group.options.length > 0)

  return (
    <>
      <Select
        control={control}
        name="service"
        label="What kind of work is it?"
        hint="So it reaches the right people. If none fits exactly, choose the closest and we will correct it."
        options={options}
        disabled={catalog.data === undefined}
        placeholder={
          failed
            ? 'The list did not load'
            : catalog.data === undefined
              ? 'Loading the rate card…'
              : 'Choose one'
        }
      />
      {failed ? (
        <Notice need role="alert">
          {messageOf(catalog.error)}
          <ButtonRow>
            <Button
              size="sm"
              disabled={catalog.isFetching}
              onClick={() => {
                void catalog.refetch()
              }}
            >
              Load the list again
            </Button>
          </ButtonRow>
        </Notice>
      ) : null}
    </>
  )
}

/**
 * The line over a group that has no one control to label — the files, the urgency — in
 * a field label's own rank and colour, on a line of its own, so the window reads as one
 * column of questions. A `Label` needs a `for`, and neither group has a control it
 * could name.
 */
function Caption({ children }: { readonly children: ReactNode }) {
  return (
    <div>
      <Small>
        <Muted>{children}</Muted>
      </Small>
    </div>
  )
}

/** One half of the card, as options. Empty for a half the read has not answered. */
function choicesOf(
  services: readonly { readonly code: string; readonly name: string }[] | undefined,
): { value: string; label: string }[] {
  return (services ?? []).map((service) => ({ value: service.code, label: service.name }))
}

/**
 * The files on the draft. A heading rather than a `Label`: the only thing a label could
 * point at is `Drop`'s hidden input, which a label cannot name usefully.
 */
function PickedFiles({
  files,
  onAdd,
  onRemove,
}: {
  readonly files: readonly PickedFile[]
  readonly onAdd: (files: readonly File[]) => void
  readonly onRemove: (key: string) => void
}) {
  const format = useFormat()
  return (
    <Field>
      <Caption>Files (optional)</Caption>
      <Drop
        label="Drop images, videos or documents here, or"
        hint="Up to 2 GB each. You can paste a screenshot, too."
        multiple
        onFiles={onAdd}
      />
      {files.length === 0 ? null : (
        <div>
          {files.map((one) => (
            <FileRow
              key={one.key}
              icon={extensionOf(one.file.name)}
              title={one.file.name}
              sub={format.bytes(one.file.size)}
              meta={
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`Remove ${one.file.name}`}
                  onClick={() => {
                    onRemove(one.key)
                  }}
                >
                  Remove
                </Button>
              }
            />
          ))}
        </div>
      )}
    </Field>
  )
}

/** How soon — the three timings, and the chosen one's line under them. */
function Urgency({
  value,
  onChange,
}: {
  readonly value: WorkOrderTiming
  readonly onChange: (value: WorkOrderTiming) => void
}) {
  return (
    <Field>
      <Caption>How urgent is it?</Caption>
      <Seg label="How urgent is it?">
        {TIMINGS.map((one) => (
          <SegItem
            key={one}
            on={value === one}
            onClick={() => {
              onChange(one)
            }}
          >
            {TIMING_WORD[one]}
          </SegItem>
        ))}
      </Seg>
      <Hint>{TIMING_HINT[value]}</Hint>
    </Field>
  )
}

/** What the window says once the request exists: its reference, and its files. */
function Receipt({
  sent,
  onPaste,
  onAdd,
  onRetry,
  onAnother,
  onLeave,
}: {
  readonly sent: Sent
  readonly onPaste: (event: ClipboardEvent<HTMLElement>) => void
  readonly onAdd: (files: readonly File[]) => void
  readonly onRetry: () => void
  readonly onAnother: () => void
  readonly onLeave: () => void
}) {
  const progress = progressOf(sent.files)
  const going = isGoing(sent.files)

  /*
   * The form the cursor was in has just gone, and a status mounted with its words
   * already in it is not reliably read out. So the receipt takes the focus, named by
   * its own first sentence — which is the reference the client will be asked about.
   */
  const named = useId()
  const self = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    self.current?.focus()
  }, [])

  return (
    <div onPaste={onPaste} ref={self} tabIndex={-1} role="group" aria-labelledby={named}>
      <Stack>
        <Notice role="status" id={named}>
          <Ref value={sent.ref} /> is with us. We price it and send it back as a decision on your
          Requests page; nothing starts, and nothing is billed, until it is approved.
        </Notice>

        {sent.files.length === 0 ? null : (
          <Stack gap="tight">
            {sent.files.map((one) => (
              <SentFileMeter key={one.key} file={one} />
            ))}
          </Stack>
        )}

        {progress.failed === 0 ? null : (
          <Notice need role="alert">
            {progress.failed === 1 ? 'One file did not' : 'Some files did not'} reach{' '}
            <Ref value={sent.ref} />. The request itself is safe.
            <ButtonRow>
              <Button disabled={going} onClick={onRetry}>
                Try {progress.failed === 1 ? 'it' : 'them'} again
              </Button>
            </ButtonRow>
          </Notice>
        )}

        <Field>
          <Caption>Forgot something?</Caption>
          <Drop
            label={`Add more files to ${sent.ref}: drop them here, or`}
            hint="Up to 2 GB each."
            multiple
            onFiles={onAdd}
          />
        </Field>

        {going ? (
          <Prose>
            You can close this window and keep working: the files go on sending, and New request
            opens this again to show how far they have got. Keep this tab open until they are done.
          </Prose>
        ) : null}

        <ButtonRow>
          {going ? null : (
            <Button variant="primary" onClick={onAnother}>
              Raise another request
            </Button>
          )}
          <Button variant="ghost" asChild>
            <Link to="/app/requests" hash="ledger" onClick={onLeave}>
              See your requests
            </Link>
          </Button>
        </ButtonRow>
      </Stack>
    </div>
  )
}

/** One file of a sent request, as a bar: how much of it has gone, and then what became of it. */
function SentFileMeter({ file }: { readonly file: SentFile }) {
  const format = useFormat()
  const size = file.file.size
  const reading =
    file.state === 'attached'
      ? 'Attached'
      : file.state === 'failed'
        ? 'Did not attach'
        : file.state === 'waiting'
          ? 'Waiting'
          : `${format.bytes(Math.min(file.sent, size))} of ${format.bytes(size)}`

  return (
    <Meter
      label={file.file.name}
      used={file.state === 'attached' ? size : Math.min(file.sent, size)}
      included={size}
      reading={reading}
      foot={file.reason ?? undefined}
    />
  )
}
